import { defineComponent as _defineComponent } from 'vue'
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { DraftsPageItem } from '@/@types/DraftsPage';
import { DeletedItemStyleClass } from '@/@types/enums/ConfirmationDeleteItem/DeletedItemStyleClass';
import EmptyStateCard from '@/components/layout/cards/EmptyStateCard.vue';
import MyTable from '@/components/layout/MyTable/MyTable.vue';
import MyTableCell from '@/components/layout/MyTable/MyTableCell.vue';
import { DRAFT_EDIT } from '@/routerNames';
import apiService from '@/services/ApiService';
import confirmationService from '@/services/ConfirmationService';
import store from '@/store';
import { dispatchGetDraftsCount, dispatchGetRetentionCount } from '@/store/dispatchers/uiDispatchers';
import { Loading } from 'element-ui';
import { debounce } from 'lodash';
import { onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';


export default /*#__PURE__*/_defineComponent({
  __name: 'DraftsPage',
  setup(__props) {

const router = useRouter();

const tableColumns = [
  { label: 'TITLE' },
  { label: 'WORKSPACE' },
  { label: 'CREATED BY' },
  { label: 'CREATION DATE', sortBy: ['createdAt'] },
  { label: 'TYPE' },
  { label: '' },
];

const drafts = ref<DraftsPageItem[]>([]);
const workspaces = ref<{ shortId: string; title: string }[]>([]);
const authors = ref<string[]>([]);

const showDialog = ref<boolean>(false);

const isLoaded = ref(false);

// Sorting
const sortOrder = ref('desc');

// Filters
const searchQuery = ref('');
const searchWorkspace = ref('');
const searchUser = ref('');
const searchType = ref('');

// Pagination
const itemPerPage = 10;
const totalPages = ref(0);
const page = ref(1);

const getClientTime = (time: number) => {
  if (!time) return '-';
  const localDate = new Date(time);
  const day = localDate.getDate().toString().padStart(2, '0');
  const month = (localDate.getMonth() + 1).toString().padStart(2, '0');
  const year = localDate.getFullYear();
  return `${day}/${month}/${year}`;
};

const openFilterDialog = () => {
  showDialog.value = true;
};

const closeFilterDialog = () => {
  showDialog.value = false;
};

const loadDrafts = async () => {
  const loader = Loading.service({ lock: true, text: 'Loading drafts' });

  try {
    const res = await apiService.draft.getAll({
      page: page.value,
      q: searchQuery.value || undefined,
      workspace: searchWorkspace.value || undefined,
      author: searchUser.value || undefined,
      type: searchType.value || undefined,
      filters: !isLoaded.value,
      sortOrder: sortOrder.value,
    });
    drafts.value = res.data.draftArticles;
    totalPages.value = Math.ceil(res.data.totalCount / itemPerPage);
    if (res.data.filters) {
      workspaces.value = res.data.filters.workspaces;
      authors.value = res.data.filters.authors;
    }
  } catch (error) {
    await apiService.handleResponseError(error);
  } finally {
    loader.close();
    loader.$destroy();
  }
};

const handleSortChange = async () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  page.value = 1;
  await loadDrafts();
};

const handleSearchInput = debounce(async () => {
  page.value = 1;
  await loadDrafts();
}, 500);

const handleFilterChange = async () => {
  page.value = 1;
  await loadDrafts();
};

const handleUpdatePage = async (p: number) => {
  page.value = p;
  await loadDrafts();
};

const handleEdit = ({ e, item }: { e?: PointerEvent | MouseEvent; item: DraftsPageItem }) => {
  e?.preventDefault();
  e?.stopPropagation();

  const isModifierPressed = e?.metaKey || e?.ctrlKey || e?.button === 1;
  const mode = item.isHasNotes ? ArticleEditMode.NOTES : ArticleEditMode.VIDEO;

  const route = { name: DRAFT_EDIT, params: { id: item.id, mode } };

  if (isModifierPressed) {
    const url = router.resolve(route).href;
    window.open(url, '_blank');
  } else {
    router.push(route);
  }
};

const handleRemove = async (item: DraftsPageItem) => {
  const result = await confirmationService
    .showRemovePopup(item.title, null, DeletedItemStyleClass.DRAFT)
    .catch(() => 'cancel');

  if (result !== 'confirm') return;

  const loader = Loading.service({ lock: true, text: 'Deleting draft' });

  try {
    await apiService.draft.delete(item.id);

    const countPromises = [dispatchGetDraftsCount()];

    if (store.state.company.retention.enabled) {
      countPromises.push(dispatchGetRetentionCount());
    }

    await Promise.all(countPromises);
    // Reload current page data
    await loadDrafts();
  } catch (e) {
    await apiService.handleResponseError(e);
  } finally {
    loader.close();
    loader.$destroy();
  }
};

onMounted(async () => {
  try {
    await loadDrafts();
    await new Promise((resolve) => setTimeout(resolve, 5000));
  } finally {
    isLoaded.value = true;
  }
});

watch([searchWorkspace, searchUser, searchType], handleFilterChange);

return { __sfc: true,router, tableColumns, drafts, workspaces, authors, showDialog, isLoaded, sortOrder, searchQuery, searchWorkspace, searchUser, searchType, itemPerPage, totalPages, page, getClientTime, openFilterDialog, closeFilterDialog, loadDrafts, handleSortChange, handleSearchInput, handleFilterChange, handleUpdatePage, handleEdit, handleRemove, EmptyStateCard, MyTable, MyTableCell }
}

})